import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import CemeteryMap from 'src/components/common/cemetery-map';
import Header from 'src/components/common/header';
import MapModal from 'src/components/common/map-modal';
import SkipLink from 'src/components/ui/skip-link';
import BankInteriorGrid from './components/bank-interior-grid';
import MapFiltersWrapper from './components/map-filters-wrapper';
import MapLots from './components/map-lots';
import MapSort from './components/map-sort';
import MausoleumPlan from './components/mausoleum-plan';
import MausoleumScheme from './components/mausoleum-scheme';

import filterStore from 'src/stores/filter-store';
import globalAppStore from 'src/stores/global-app-store';
import LotsViewStore from 'src/stores/lots-view-store';
import MapStore from 'src/stores/map-store';

import { IS_MAUSOLEUM_VIEW, LotStatus, MAP_DEFAULT_ZOOM } from 'src/constants';
import { useHeader } from 'src/contexts/header-context';
import { useLocalization } from 'src/contexts/localization-context';
import useIsLaptop from 'src/hooks/use-is-laptop';
import useWindowSize from 'src/hooks/use-window-size';
import type { ILot } from 'src/interfaces';

import './map-view-page.scss';

const FILTERS_DESKTOP_HEIGHT = 70;

const MapViewPage = observer(() => {
  const { clearStore, init } = filterStore;
  const [store] = useState(() => new LotsViewStore());
  const {
    bankOptions,
    inGroundLots,
    isLoading,
    mausoleumCoords,
    selectedLot,
    visibleListLots,
    visibleMapLots,
    resetSelectedLot,
    setSelectedLot,
  } = store;

  const isLaptop = useIsLaptop();
  const windowSize = useWindowSize();
  const { selectedBank } = filterStore;

  const handleSelectLot = useCallback(
    (lot: ILot) => {
      if (lot.status === LotStatus.UNAVAILABLE) {
        return;
      }

      if (IS_MAUSOLEUM_VIEW && !selectedBank && lot.bankId) {
        filterStore.handleChangeLocation(lot.bankId);
      }

      setSelectedLot(lot);
    },
    [selectedBank, setSelectedLot]
  );

  const { cemetery } = globalAppStore;
  const { getLocalization } = useLocalization();
  const { headerHeight } = useHeader();
  const mapHeight = `${windowSize.height - headerHeight - FILTERS_DESKTOP_HEIGHT}px`;
  const { cemeteryPaths = [], tileUrl = '', wmsUrl = '', coords, mausoleums = [] } = cemetery || {};

  const [mapStore, setMapStore] = useState<MapStore | undefined>(undefined);

  const handleMapReset = useCallback(() => {
    mapStore?.handleHomeButtonClick(MAP_DEFAULT_ZOOM);
  }, [mapStore]);

  useEffect(() => {
    if (IS_MAUSOLEUM_VIEW) {
      return;
    }

    if (isLaptop && coords) {
      setMapStore(new MapStore(coords, cemeteryPaths, tileUrl, wmsUrl));
    } else {
      setMapStore(undefined);
    }
  }, [cemeteryPaths, coords, isLaptop, tileUrl, wmsUrl]);

  useEffect(() => {
    init();

    return () => clearStore();
  }, [clearStore, init]);

  const mausoleumWithPlan = mausoleums.find((el) => el.plan);

  return (
    <div className="map-view-page">
      <SkipLink href="#main" label="Skip to Lots List" position="fixed" />
      <Header actions="phone" isFullWidth />

      <MapFiltersWrapper bankOptions={bankOptions} onMapReset={handleMapReset} />

      <main
        className={classNames(
          'map-view-page__content',
          selectedBank && 'map-view-page__content_scheme'
        )}
        id="main"
      >
        <div className="map-view-page__list">
          <div className="map-view-page__list-wrapper">
            <h1 className="map-view-page__list-count">
              {`${getLocalization('Lots Found')}:`}{' '}
              <span className="map-view-page__list-count-value">{visibleListLots.length}</span>
            </h1>
            <MapSort className="map-view-page__sort" isDisabled={isLoading} />
          </div>
          <MapLots
            className="map-view-page__list-lots"
            isLoading={isLoading}
            lots={visibleListLots}
            selectedLot={selectedLot}
            handleCardClick={handleSelectLot}
          />
        </div>

        {isLaptop && IS_MAUSOLEUM_VIEW ? (
          <>
            {selectedBank ? (
              <BankInteriorGrid
                className="map-view-page__map"
                height={mapHeight}
                lots={visibleMapLots}
                selectedLot={selectedLot}
                onLotClick={handleSelectLot}
              />
            ) : mausoleumWithPlan ? (
              <MausoleumPlan
                className="map-view-page__map"
                height={mapHeight}
                mausoleum={mausoleumWithPlan}
                onSelectBank={filterStore.handleChangeLocation}
              />
            ) : (
              <MausoleumScheme
                className="map-view-page__map"
                bankOptions={bankOptions}
                height={mapHeight}
                onSelectBank={filterStore.handleChangeLocation}
              />
            )}
            {selectedLot && (
              <div className="map-view-page__mausoleum-popup" onClick={resetSelectedLot}>
                <div onClick={(e) => e.stopPropagation()}>
                  <MapModal lot={selectedLot} />
                </div>
              </div>
            )}
          </>
        ) : isLaptop && coords && mapStore ? (
          <CemeteryMap
            center={coords}
            cemeteryPaths={cemeteryPaths}
            height={mapHeight}
            lots={inGroundLots}
            mapStore={mapStore}
            mausoleumsCoords={mausoleumCoords}
            selectedLot={selectedLot}
            width="100%"
            handleLotMarkerClick={handleSelectLot}
            resetSelectedLot={resetSelectedLot}
          />
        ) : null}
      </main>
    </div>
  );
});

export default MapViewPage;
